/**
 * @description This renders the body content of the FAQ page, including the category list on the left and the
 *  questions/answers list on the right.
 */

import React from 'react';
import PropTypes from 'prop-types';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';

import NoResults from '@seccon/shared/ui/components/NoResults';

const styles = {
  container: {
    display: 'flex',
    flexDirection: {
      lg: 'row',
      xs: 'column'
    },
    flexWrap: 'nowrap',
    gap: {
      lg: '10%',
      xs: 3
    },
    justifyContent: {
      lg: 'space-between',
      xs: 'flex-start'
    },
    paddingX: {
      lg: '5%',
      xs: 0
    },
    width: '100%'
  },
  contentContainer: {
    border: 1,
    borderColor: 'grey.400',
    borderRadius: 1,
    boxShadow: 1,
    height: 'fit-content',
    padding: 0,

    '&.noResults, &.loading': {
      border: 0,
      boxShadow: 0
    },

    '&.left': {
      width: {
        lg: '30%',
        xs: '100%'
      }
    },

    '&.right': {
      width: {
        lg: '60%',
        xs: '100%'
      }
    }
  },
  noResults: {
    fontSize: '2rem'
  }
};

const responseStyles = {
  accordion: {
    borderTop: (theme) => `solid ${ theme.palette.grey['400'] } 1px`,
    boxShadow: 'none',

    '&.MuiAccordion-root:before': {
      content: 'none'
    }
  },
  answerText: {
    fontWeight: 'light',
    whiteSpace: 'pre-line'
  },
  expandIcon: {
    color: '#D96729'
  },
  header: {
    display: 'block',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    marginY: 1,
    textAlign: 'center',
    width: '100%'
  },
  noResults: {
    fontSize: '1rem'
  },
  questionText: {
    fontWeight: 'bold'
  }
};

const categoryStyles = {
  button: {
    '&:hover': {
      backgroundColor: '#D96729',
      color: 'primary.contrastText'
    },

    '&.Mui-selected': {
      backgroundColor: 'primary.main',
      color: 'primary.contrastText',

      '&:hover': {
        backgroundColor: '#D96729'
      }
    }
  },
  header: {
    display: 'flex',
    justifyContent: 'center',
    marginY: 1,
    width: '100%',

    '& .MuiListItemText-primary': {
      fontSize: '1.25rem',
      fontWeight: 'bold'
    }
  },
  item: {
    borderBottom: 1,
    borderColor: 'grey.400',
    padding: 0,

    '&:last-of-type': {
      borderBottom: 0
    }
  },
  text: {
    textAlign: 'center',

    '& .MuiListItemText-primary': {
      fontWeight: 'bold'
    }
  }
};

/**
 * @prop {{ id: string, category: string }[]} - list of FAQ categories
 * @prop {{ question: string, answer: string }[]} - list of question/answer responses
 * @prop {string} selectedCategory - the currently selected category
 * @prop {(string) => void} categorySelectCallback - a callback function used when a new category has been selected
 */
const FaqSection = (props) => {
  const categories = props.categories;
  const responses = props.responses;
  const isLoadingCategories = props.isLoadingCategories;
  const isLoadingResponses = props.isLoadingResponses;
  const hasNoCategories = !isLoadingCategories && categories.length === 0;

  function renderCategories () {
    if (isLoadingCategories) {
      return (
        <Stack className='left loading' spacing={2} sx={styles.contentContainer}>
          <Skeleton animation='wave' variant='rectangular' height={32} />
          <Skeleton animation='wave' variant='rectangular' height={32} />
          <Skeleton animation='wave' variant='rectangular' height={32} />
          <Skeleton animation='wave' variant='rectangular' height={32} />
          <Skeleton animation='wave' variant='rectangular' height={32} />
        </Stack>
      );
    }

    return (
      <List className='left' sx={styles.contentContainer}>
        <ListItem sx={categoryStyles.item}>
          <ListItemText primary='Category' sx={categoryStyles.header} />
        </ListItem>
        {categories.map((category) => (
          <ListItem key={category.id} sx={categoryStyles.item}>
            <ListItemButton
              id={category.id}
              selected={props.selectedCategory === category.id}
              onClick={() => { props.categorySelectCallback(category.id) }}
              sx={categoryStyles.button}
            >
              <ListItemText primary={category.category} sx={categoryStyles.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  }

  function renderResponses () {
    if (isLoadingResponses || isLoadingCategories) {
      return (
        <Stack className='right loading' spacing={2} sx={styles.contentContainer}>
          <Skeleton animation='wave' variant='rectangular' height={32} />
          <Skeleton animation='wave' variant='rectangular' height={32} />
          <Skeleton animation='wave' variant='rectangular' height={32} />
          <Skeleton animation='wave' variant='rectangular' height={32} />
          <Skeleton animation='wave' variant='rectangular' height={32} />
        </Stack>
      );
    }

    if (responses.length === 0) {
      return (
        <Box className='right noResults' sx={styles.contentContainer}>
          <NoResults Icon={SentimentDissatisfiedIcon} sx={responseStyles.noResults}>No Questions and Answers Found</NoResults>
        </Box>
      );
    }

    return (
      <Box className='right' sx={styles.contentContainer}>
        <Typography sx={responseStyles.header}>Q&As</Typography>
        {responses.map((resp) => (
          <Accordion disableGutters key={resp.question} square sx={responseStyles.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={responseStyles.expandIcon} />} sx={responseStyles.questionText}>
              { resp.question }
            </AccordionSummary>
            <AccordionDetails sx={responseStyles.answerText}>
              { resp.answer }
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      {hasNoCategories ? (
        <NoResults Icon={SentimentDissatisfiedIcon} sx={styles.noResults}>No FAQs Found</NoResults>
      ) : (
        <>
          {renderCategories()}
          {renderResponses()}
        </>
      )}
    </Box>
  )
}

FaqSection.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.object).isRequired,
  responses: PropTypes.arrayOf(PropTypes.object).isRequired,
  categorySelectCallback: PropTypes.func.isRequired,
  selectedCategory: PropTypes.number.isRequired,
  isLoadingCategories: PropTypes.bool.isRequired,
  isLoadingResponses: PropTypes.bool.isRequired
};

export default FaqSection;
